import React, { useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { StatsPage, type StatsData, type StatsPageLabels } from '../ui/diary/StatsPage';
import type { StatsFilters, CategoryOption } from '../ui/diary/StatsFilters';

// Create a query client for this component tree
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

export interface StatsPageContainerProps {
  graphqlEndpoint: string;
  csrfToken: string;
  exportCsvUrl: string;
  reportsUrl: string;
  influencingFieldsUrl: string;
  diaryUrl: string;
  initialData?: StatsData;
  initialFilters?: StatsFilters;
  initialCategories?: CategoryOption[];
  defaultPeriodStart: string;
  defaultPeriodEnd: string;
  labels: StatsPageLabels;
}

// GraphQL Query
const GET_DIARY_STATS_QUERY = `
  query GetDiaryStats($fieldIds: [ID!], $periodStart: String, $periodEnd: String) {
    diaryStats(fieldIds: $fieldIds, periodStart: $periodStart, periodEnd: $periodEnd) {
      empty
      moodByDate
      averageMoodPerWeekday
      activitiesPercentagePerWeekday
      totalAverageMood
      medianMood
      mostInfluencingFields {
        name
        averageMood
        influence
        occurrences
      }
      oldestEntryDate
      numericDependencies {
        id
        name
        color
        dataPoints {
          rangeLabel
          averageMood
          count
        }
      }
      unviewedReportsCount
      categories {
        text
        children {
          id
          text
          icon
          selected
        }
      }
      hasFieldIds
    }
  }
`;

function StatsPageInner({
  graphqlEndpoint,
  csrfToken,
  exportCsvUrl,
  reportsUrl,
  influencingFieldsUrl,
  diaryUrl,
  initialData,
  initialFilters,
  initialCategories,
  defaultPeriodStart,
  defaultPeriodEnd,
  labels,
}: StatsPageContainerProps) {
  const [filters, setFilters] = useState<StatsFilters>(
    initialFilters || {
      fieldIds: [],
      periodStart: defaultPeriodStart,
      periodEnd: defaultPeriodEnd,
    }
  );

  // Fetch stats data
  const { data: statsData, isLoading, refetch } = useQuery({
    queryKey: ['diaryStats', filters.fieldIds, filters.periodStart, filters.periodEnd],
    queryFn: async () => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: GET_DIARY_STATS_QUERY,
          variables: {
            fieldIds: filters.fieldIds.length > 0 ? filters.fieldIds : null,
            periodStart: filters.periodStart,
            periodEnd: filters.periodEnd,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      // Transform the GraphQL response to match component expectations
      const stats = result.data.diaryStats;
      return {
        empty: stats.empty,
        moodByDate: stats.moodByDate,
        averageMoodPerWeekday: stats.averageMoodPerWeekday,
        activitiesPercentagePerWeekday: stats.activitiesPercentagePerWeekday,
        totalAverageMood: stats.totalAverageMood,
        medianMood: stats.medianMood,
        mostInfluencingFields: stats.mostInfluencingFields,
        oldestEntryDate: stats.oldestEntryDate,
        numericDependencies: stats.numericDependencies.map((field: any) => ({
          id: field.id,
          name: field.name,
          color: field.color,
          dataPoints: field.dataPoints.map((dp: any) => ({
            rangeLabel: dp.rangeLabel,
            averageMood: dp.averageMood,
            count: dp.count,
          })),
        })),
        unviewedReportsCount: stats.unviewedReportsCount,
        hasFieldIds: stats.hasFieldIds,
        categories: stats.categories,
      } as StatsData & { categories: CategoryOption[] };
    },
    initialData: initialData ? { ...initialData, categories: initialCategories || [] } : undefined,
    initialDataUpdatedAt: initialData ? Date.now() : undefined,
  });

  const handleFiltersChange = useCallback((newFilters: StatsFilters) => {
    setFilters(newFilters);
  }, []);

  if (!statsData) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Extract categories from the response or use initial
  const categories = (statsData as any).categories || initialCategories || [];

  return (
    <StatsPage
      data={statsData}
      filters={filters}
      categories={categories}
      onFiltersChange={handleFiltersChange}
      exportCsvUrl={exportCsvUrl}
      reportsUrl={reportsUrl}
      influencingFieldsUrl={influencingFieldsUrl}
      diaryUrl={diaryUrl}
      defaultPeriodStart={defaultPeriodStart}
      defaultPeriodEnd={defaultPeriodEnd}
      labels={labels}
      isLoading={isLoading}
    />
  );
}

/**
 * StatsPageContainer - Entry point for rendering stats page in Rails
 */
export function StatsPageContainer(props: StatsPageContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <StatsPageInner {...props} />
    </QueryClientProvider>
  );
}

/**
 * Mount function for rendering from Rails
 */
export function mountStatsPage(
  container: HTMLElement,
  props: StatsPageContainerProps
) {
  const root = createRoot(container);
  root.render(<StatsPageContainer {...props} />);
  return root;
}

export default StatsPageContainer;
